import { useState } from 'react';
import { useTranslate } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { PublisherScopedListTitle } from '@/components/content_catalog/PublisherScopedListTitle';
import { usePublisherScope } from '@/hooks/usePublisherScope';
import {
  Table,
  Input,
  Tag,
  Form,
  Row,
  Col,
  Flex,
  Typography,
  Button,
  theme,
} from 'antd';
import { ProductBulkActions } from '@/components/content_catalog/products/ProductBulkActions';
import { ProductAssetsCell } from '@/components/content_catalog/products/ProductAssetsCell';
import { ProductAssetsFilter } from '@/components/content_catalog/products/ProductAssetsFilter';
import { SearchOutlined } from '@ant-design/icons';
import type { ContentProductListOut } from '@/api/generated/schemas';
import { ProductType } from '@/api/generated/schemas';
import type { CrudFilter } from '@refinedev/core';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import { useDragRowSelection } from '@/hooks/useDragRowSelection';
import {
  publishingStatusColors,
  productTypeColors,
  availabilityColors,
  ASSET_FILTER_FIELDS,
} from './constants';
import { ContentProductListOutAvailability } from '@/api/generated/schemas';
import { SelectFilter } from '@/components/common/SelectFilter';
import { BooleanFilter } from '@/components/common/BooleanFilter';
import { PresencePill } from '@/components/common/PresencePill';
import { ShowLink } from '@/components/common/ShowLink';
import { ActiveFilters } from '@/components/common/ActiveFilters';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

export const ProductList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();
  const { token } = theme.useToken();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, sorters, filters, setFilters } =
    useAdminTable<ContentProductListOut>({
      pagination: { pageSize: 20 },
      resource: 'content_catalog.products',
      onSearch,
      sorters: {
        initial: [{ field: 'id', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const rows = (tableProps.dataSource ?? []) as ContentProductListOut[];
  // Selection is of visible rows: an id left behind by a page or filter
  // change would inflate the count while the action acted on fewer products.
  const visibleSelectedIds = selectedIds.filter((id) =>
    rows.some((r) => r.id === id)
  );
  // Press a checkbox and drag to select (or deselect) a run of rows.
  const drag = useDragRowSelection<number>({
    keys: rows.map((r) => r.id),
    selected: visibleSelectedIds,
    onChange: setSelectedIds,
  });
  // A bulk run outlives the selection it started from — see the bulk bar.
  const [bulkOpen, setBulkOpen] = useState(false);
  const titleById = Object.fromEntries(
    rows.filter((r) => r.title).map((r) => [r.id, r.title as string])
  ) as Record<number, string>;

  // The asset facets are labelled as the "Assets" column names them.
  const assetField: Record<string, string> = {
    is_cover_downloaded: 'cover',
    is_master_downloaded: 'master',
  };
  const productFilterField = (field: string) =>
    field in assetField
      ? t(`content_catalog.products.assets.${assetField[field]}`)
      : undefined;
  const productFilterValue = (
    field: string,
    value: string | number | boolean
  ) => {
    if (typeof value === 'boolean')
      return t(value ? 'common.yes' : 'common.no');
    if (field === 'product_type')
      return t(`content_catalog.products.product_type.${value}`);
    if (field === 'publishing_status')
      return t(`content_catalog.products.publishing_status.${value}`);
    if (field === 'availability')
      return t(`content_catalog.distribution_periods.filters.${value}`);
    // No longer a column, but a bookmarked URL can still carry it.
    if (field === 'transcoding_status')
      return t(`content_catalog.transcoding_jobs.status.${value}`);
    return undefined;
  };

  const { publisherId, setPublisherScope } = usePublisherScope(
    filters as CrudFilter[],
    setFilters,
    () => setSelectedIds([])
  );
  const total =
    typeof tableProps.pagination === 'object'
      ? tableProps.pagination.total
      : undefined;

  return (
    <List
      title={
        <PublisherScopedListTitle
          title={t('content_catalog.products.titles.list')}
          total={total}
          totalKey="content_catalog.products.context.total"
          publisherId={publisherId}
          onPublisherChange={setPublisherScope}
        />
      }
    >
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="middle">
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('content_catalog.products.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <ActiveFilters
        filters={filters as CrudFilter[]}
        setFilters={setFilters}
        labelPrefix="content_catalog.products"
        // The switcher and the search box speak for these two.
        ignore={['publisher_id', 'q']}
        baseFields={['created_at', 'updated_at']}
        formatField={productFilterField}
        formatValue={productFilterValue}
      />

      {/* The bar also stays while the modal is open: the refetch after a run
          can drop acted-on rows out of the view, and unmounting here would
          take the result view with it. */}
      {(visibleSelectedIds.length > 0 || bulkOpen) && (
        <Flex
          align="center"
          gap={token.marginSM}
          style={{ marginBottom: token.marginSM }}
        >
          {visibleSelectedIds.length > 0 && (
            <Typography.Text>
              {t('content_catalog.products.bulk.selected', {
                count: visibleSelectedIds.length,
              })}
            </Typography.Text>
          )}
          <ProductBulkActions
            ids={visibleSelectedIds}
            titles={titleById}
            onClosed={() => setSelectedIds([])}
            onOpenChange={setBulkOpen}
          />
          <Button type="link" size="small" onClick={() => setSelectedIds([])}>
            {t('actions.clear')}
          </Button>
        </Flex>
      )}

      <Table
        {...rowNav('content_catalog.products', {
          ...tableProps,
          onRow: drag.onRow,
        })}
        rowSelection={{
          selectedRowKeys: drag.selectedRowKeys,
          onChange: (keys) => setSelectedIds(keys.map(Number)),
        }}
        rowKey="id"
        pagination={{
          ...(typeof tableProps.pagination === 'object'
            ? tableProps.pagination
            : {}),
          showTotal: (total) => t('base.total', { total }),
        }}
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="title"
          title={t('content_catalog.products.fields.title')}
          sorter
          sortOrder={getSortOrder(sorters, 'title')}
        />

        <Table.Column
          dataIndex="isbn"
          title={t('content_catalog.products.fields.isbn')}
          sorter
          sortOrder={getSortOrder(sorters, 'isbn')}
        />

        <Table.Column
          dataIndex="product_type"
          title={t('content_catalog.products.fields.product_type')}
          render={(value: string) =>
            value ? (
              <Tag color={productTypeColors[value] || 'default'}>
                {t(`content_catalog.products.product_type.${value}`)}
              </Tag>
            ) : (
              '-'
            )
          }
          filtered={isFieldFiltered(filters as CrudFilter[], 'product_type')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="product_type"
              placeholder={t('content_catalog.products.fields.product_type')}
              options={Object.values(ProductType).map((v) => ({
                label: t(`content_catalog.products.product_type.${v}`),
                value: v,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="publishing_status"
          title={t('content_catalog.products.fields.publishing_status')}
          render={(value: string) =>
            value ? (
              <Tag color={publishingStatusColors[value] || 'default'}>
                {t(`content_catalog.products.publishing_status.${value}`)}
              </Tag>
            ) : (
              '-'
            )
          }
          filtered={isFieldFiltered(
            filters as CrudFilter[],
            'publishing_status'
          )}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="publishing_status"
              placeholder={t(
                'content_catalog.products.fields.publishing_status'
              )}
              options={[
                'announced',
                'available',
                'unavailable',
                'postponed',
                'cancelled',
                'deleted',
              ].map((v) => ({
                label: t(`content_catalog.products.publishing_status.${v}`),
                value: v,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="availability"
          title={t('content_catalog.products.fields.availability')}
          sorter
          sortOrder={getSortOrder(sorters, 'availability')}
          render={(value: string | null) =>
            value ? (
              <Tag color={availabilityColors[value] || 'default'}>
                {t(`content_catalog.distribution_periods.filters.${value}`)}
              </Tag>
            ) : (
              '-'
            )
          }
          filtered={isFieldFiltered(filters as CrudFilter[], 'availability')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="availability"
              placeholder={t('content_catalog.products.fields.availability')}
              options={Object.values(ContentProductListOutAvailability).map(
                (v) => ({
                  label: t(`content_catalog.distribution_periods.filters.${v}`),
                  value: v,
                })
              )}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column<ContentProductListOut>
          key="assets"
          title={t('content_catalog.products.assets.title')}
          render={(_, record) => <ProductAssetsCell record={record} />}
          filtered={ASSET_FILTER_FIELDS.some((field) =>
            isFieldFiltered(filters as CrudFilter[], field)
          )}
          filterDropdown={({ confirm, clearFilters }) => (
            <ProductAssetsFilter
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column<ContentProductListOut>
          dataIndex="publisher_id"
          title={t('content_catalog.products.fields.publisher_name')}
          render={(_, record) =>
            record.publisher_name ? (
              <Flex vertical>
                {record.publisher_id != null ? (
                  <ShowLink
                    resource="content_catalog.publishers"
                    id={record.publisher_id}
                  >
                    {record.publisher_name}
                  </ShowLink>
                ) : (
                  <span>{record.publisher_name}</span>
                )}
                {record.imprint_name && (
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: token.fontSizeSM }}
                  >
                    {t('content_catalog.products.fields.imprint')}:{' '}
                    {record.imprint_id != null ? (
                      <ShowLink
                        resource="content_catalog.imprints"
                        id={record.imprint_id}
                      >
                        {record.imprint_name}
                      </ShowLink>
                    ) : (
                      record.imprint_name
                    )}
                  </Typography.Text>
                )}
              </Flex>
            ) : (
              '-'
            )
          }
          // No column filter: the publisher scope is owned by the context
          // switcher in the page title.
        />

        <Table.Column
          dataIndex="publisher_has_active_contract"
          title={t(
            'content_catalog.products.fields.publisher_has_active_contract'
          )}
          sorter
          sortOrder={getSortOrder(sorters, 'publisher_has_active_contract')}
          render={(value: boolean | undefined) => (
            <PresencePill
              present={!!value}
              label={t(
                'content_catalog.products.fields.publisher_has_active_contract'
              )}
            />
          )}
          filtered={isFieldFiltered(
            filters as CrudFilter[],
            'publisher_has_active_contract'
          )}
          filterDropdown={({ confirm, clearFilters }) => (
            <BooleanFilter
              field="publisher_has_active_contract"
              placeholder={t(
                'content_catalog.products.fields.publisher_has_active_contract'
              )}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'created_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="created_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />

        <Table.Column
          dataIndex="updated_at"
          title={t('base.fields.updated_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'updated_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'updated_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="updated_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string | null) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />
      </Table>
    </List>
  );
};
